import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { useTheme } from "../../context/ThemeContext";
import { tc } from "../../lib/theme";

const PALETTE = ["var(--c-accent)", "var(--c-green)", "var(--c-amber)", "var(--c-red)", "#7c3aed", "#0891b2", "#db2777",
                 "#65a30d", "#ea580c", "#4f46e5", "#0d9488", "#a16207", "#be185d", "#475569"];

export const inr = (v) => (v === null || v === undefined) ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;

export const CostDot = ({ source }) => (
  <span className="inline-block w-2 h-2 rounded-full ml-1" title={
    source === "vendor_quote" ? "Vendor quote (firm)" : source === "standard_cost_db" ? "Standard cost DB (estimate)" : "No cost data"}
    style={{ background: source === "vendor_quote" ? "var(--c-green)" : source === "standard_cost_db" ? "var(--c-amber)" : "var(--c-red)" }} />
);

export default function CostSummary({ summary, categories, template }) {
  useTheme();
  const pal = [tc("accent"), tc("green"), tc("amber"), tc("red"), "#7c3aed", "#0891b2", "#db2777",
               "#65a30d", "#ea580c", "#4f46e5", "#0d9488", "#a16207", "#be185d", "#475569"];
  if (!summary) return null;
  const pct = summary.item_count ? Math.round(100 * summary.costed_count / summary.item_count) : 0;
  const pieData = template.map((t, i) => ({
    name: t.name, value: (categories?.[t.id]?.cost) || 0, color: pal[i % pal.length],
  })).filter((d) => d.value > 0);

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4 flex gap-6 items-center" data-testid="cost-summary">
      <div className="space-y-1 min-w-[260px]">
        <div className="text-lg text-[var(--c-text)] font-medium" data-testid="total-cost">{inr(summary.total_cost)}</div>
        <div className="text-[11px] text-[var(--c-muted)]">Costed items: {summary.costed_count}/{summary.item_count} ({pct}%)</div>
        <div className="text-[11px] text-[var(--c-muted)] flex items-center gap-1">From vendor quotes: {inr(summary.firm_cost)} <CostDot source="vendor_quote" /></div>
        <div className="text-[11px] text-[var(--c-muted)] flex items-center gap-1">From estimates: {inr(summary.estimate_cost)} <CostDot source="standard_cost_db" /></div>
        {summary.item_count - summary.costed_count > 0 && (
          <div className="text-[11px] text-[var(--c-amber)] mt-1">⚠ {summary.item_count - summary.costed_count} items have no cost data.</div>
        )}
        <div className="text-[11px] text-[var(--c-muted)]">Total weight: {Number(summary.total_weight || 0).toLocaleString()} kg</div>
      </div>
      {pieData.length > 0 && (
        <div className="w-56 h-44 min-w-[224px] min-h-[176px]" data-testid="cost-pie">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={32} outerRadius={62} strokeWidth={1} stroke={tc("bg")}>
                {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "var(--c-surface)", border: "1px solid var(--c-border)", fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1 max-h-44 overflow-y-auto">
        {pieData.map((d) => (
          <div key={d.name} className="flex items-center gap-1.5 text-[10px]">
            <span className="w-2 h-2 rounded-[2px] shrink-0" style={{ background: d.color }} />
            <span className="text-[var(--c-muted)] truncate">{d.name}</span>
            <span className="ml-auto font-mono text-[var(--c-text)]">{inr(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
